import React from 'react';

import { THEME_BY_ID, polarityOf } from '../../themes';
import './ThemePreview.css';

/**
 * En miniatyr av sajten i ett visst tema.
 *
 * Finns för butiken: ett tema kostar 500–1500 poäng, och tre färgprickar
 * räcker inte som beslutsunderlag för den summan. Miniatyren visar det man
 * faktiskt köper — sidbakgrund, kortyta, rubriktypsnitt, accenter och
 * texthierarki.
 *
 * INGA HÅRDKODADE FÄRGER. Elementet bär `data-theme`, vilket aktiverar temats
 * eget variabelblock på just den här noden, så all CSS inuti läser temats
 * riktiga tokens via `var()`. Det betyder att previewen aldrig kan glida isär
 * från paletten — den ÄR paletten, renderad. `data-polarity` sätts av samma
 * skäl: regler som beror på ljus/mörk botten ska gälla här också.
 *
 * Skiljer sig med flit från temaväljarens tre prickar. Väljaren är ett tätt
 * rutnät med plats för 18 alternativ; butikskortet har utrymme att visa hela
 * bilden och ett behov av att göra det.
 *
 * @param {object} props
 * @param {string} props.themeId - tema-id, samma värde som `data-theme`.
 * @param {string} [props.label] - varans namn, används som tooltip.
 * @returns {JSX.Element} Miniatyren, eller en tom platta för okänt tema.
 */
export default function ThemePreview({ themeId, label }) {
  const theme = THEME_BY_ID[themeId];

  // Butiken listas av backendkatalogen och paletterna bor i frontend. Ligger
  // en frontend efter — temat är släppt i API:t men filen finns inte i den
  // här bundlen — matchar `data-theme` ingenting, och previewen hade tyst
  // renderat i det AKTIVA temats färger. Alltså: visa att den inte kan visas.
  if (!theme) {
    return (
      <div className="theme-preview theme-preview--unknown" title={label}>
        ?
      </div>
    );
  }

  return (
    /* Nollställaren är inte överflödig — ta inte bort den.
     *
     * Ett temablock sätter bara de tokens temat faktiskt deklarerar. Resten
     * ÄRVS, och inuti en sida som redan har ett tema ärvs de från det temat,
     * inte från :root. Mätt: en Mirage-preview visad inifrån Mushroom Kingdom
     * renderade sin rubrik i pixelfont, eftersom mirage.css inte deklarerar
     * --font-display. Previewen ljög alltså om temat den visade.
     *
     * `[data-theme="dark"]` deklarerar HELA basuppsättningen (den är :root),
     * så ett lager med den återställer allt innan temats overrides läggs på.
     * display:contents gör lagret osynligt för layouten. */
    <div className="theme-preview-reset" data-theme="dark">
      <div
        className="theme-preview"
        data-theme={themeId}
        data-polarity={polarityOf(themeId)}
        title={label}
        aria-hidden="true"
      >
        <div className="theme-preview-topbar">
          <span className="theme-preview-brand">CS2</span>
          <span className="theme-preview-dot theme-preview-dot--orange" />
          <span className="theme-preview-dot theme-preview-dot--blue" />
        </div>

        <div className="theme-preview-card">
          {/* Temats eget namn, inte varans. Butikstiteln är "Tema: Mirage"
              och hade ellipsats bort just den del som betyder något. */}
          <div className="theme-preview-heading">{theme.label}</div>
          <div className="theme-preview-line theme-preview-line--secondary" />
          <div className="theme-preview-line theme-preview-line--muted" />
          <div className="theme-preview-chips">
            <span className="theme-preview-chip theme-preview-chip--orange" />
            <span className="theme-preview-chip theme-preview-chip--gold" />
            <span className="theme-preview-chip theme-preview-chip--green" />
          </div>
        </div>
      </div>
    </div>
  );
}
